import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import axios from 'axios';

// Import your base URL and Action
import { baseURL } from '../../actions/type'; 
import { getcandidate } from '../../actions/candidateAction'; 

// --- CSS Styles (You can copy this to a CSS file) ---
const styles = {
    container: { padding: '20px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f4f6f8' },
    sectionTitle: { borderBottom: '2px solid #007bff', paddingBottom: '10px', marginTop: '30px', color: '#007bff' },
    
    // Grid layout for cards
    grid: { display: 'flex', flexWrap: 'wrap', gap: '15px' },
    
    // Card Style
    card: {
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '15px',
        width: '100%',
        maxWidth: '350px', // Limits width for desktop
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    },
    
    // Text Styles
    cardHeader: { fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: '#333' },
    row: { display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '14px' },
    label: { fontWeight: '600', color: '#666' },
    value: { color: '#333', fontWeight: '500' },
    
    // Badges for Status
    badge: {
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: '#e3f2fd',
        color: '#0d47a1',
        display: 'inline-block',
        marginTop: '5px'
    }
};

// --- 1. Meeting Card Component ---
const MeetingCard = ({ item }) => (
    <div style={styles.card}>
        <div style={styles.cardHeader}>{item.title}</div>
        
        <div style={styles.row}>
            <span style={styles.label}>Venue:</span>
            <span style={styles.value}>{item.venue}</span>
        </div>
        <div style={styles.row}>
            <span style={styles.label}>Date:</span>
            <span style={styles.value}>{item.date}</span>
        </div>
        <div style={styles.row}>
            <span style={styles.label}>Time:</span>
            <span style={styles.value}>{item.time}</span>
        </div>
    </div>
);

// --- 2. Candidate Card Component (Updated with your specific fields) ---
const CandidateCard = ({ item }) => (
    <div style={styles.card}>
        <div style={styles.cardHeader}>
            {item.fullName}
        </div>

        {/* Party and Type */}
        <div style={styles.row}>
            <span style={styles.label}>Party:</span>
            <span style={styles.value}>{item.partyName || 'N/A'}</span>
        </div>
        
        {/* Mobile */}
        <div style={styles.row}>
            <span style={styles.label}>Mobile:</span>
            <span style={styles.value}>{item.mobile}</span>
        </div>

        {/* Gender & Caste */}
        <div style={styles.row}>
            <span style={styles.label}>Details:</span>
            <span style={styles.value}>
                {item.gender || '-'} / {item.caste || '-'}
            </span>
        </div>

        {/* Election Type / Status */}
        <div style={{ marginTop: '10px' }}>
            <span style={styles.badge}>
                {item.etype || 'Unknown Status'}
            </span>
        </div>
    </div>
);

const CandidateDashboard = ({ user, token, getcandidate, candidateData }) => {
    const { t } = useTranslation();
    const [meetings, setMeetings] = useState([]);
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(true);

    // --- Fetch Meetings Locally ---
    const fetchMeetingsForCandidate = useCallback(async () => {
        if (!user || !user.id || !token) {
            setIsLoadingMeetings(false);
            return;
        }

        try {
            const payload = { uid: user.id };
            const res = await axios.post(`${baseURL}/api/electionmeetings/meeting`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Ensure we use the data array
            setMeetings(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching meetings:", err);
            setMeetings([]); 
        } finally {
            setIsLoadingMeetings(false);
        }
    }, [user, token]);

    // --- Effect: Call APIs on Load ---
    useEffect(() => {
        if (user && user.id) {
            // 1. Call Meeting API
            fetchMeetingsForCandidate();

            // 2. Call Candidate API (Redux Action)
            const candidatePayload = { uid: user.id }; 
            console.log("Calling getcandidate with:", candidatePayload);
            getcandidate(candidatePayload);
        }
    }, [fetchMeetingsForCandidate, getcandidate, user]);

    // --- Render Content ---
    if (!user || !user.id) {
        return <div style={{ padding: '20px' }}>Loading User Profile...</div>;
    }

    return (
        <div style={styles.container}>
            <h1 style={{ marginBottom: '20px' }}>Dashboard</h1>

            {/* --- Section 1: Candidate List --- */}
            <h2 style={styles.sectionTitle}>Candidates List</h2>
            <div style={styles.grid}>
                {candidateData && Array.isArray(candidateData) && candidateData.length > 0 ? (
                    candidateData.map((item) => (
                        <CandidateCard key={item.id} item={item} />
                    ))
                ) : (
                    <p>No candidate data available.</p>
                )}
            </div>

            {/* --- Section 2: Meetings List --- */}
            <h2 style={styles.sectionTitle}>{t('yourMeetings')}</h2>
            <div style={styles.grid}>
                {isLoadingMeetings ? (
                    <p>Loading Meetings...</p>
                ) : meetings.length > 0 ? (
                    meetings.map((item) => (
                        <MeetingCard key={item.id} item={item} />
                    ))
                ) : (
                    <p>{t('noMeetingsScheduled')}</p>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.auth.user,
    token: state.auth.token,
    // Accessing the data from the Redux store
    candidateData: state.candidate.getcandidate 
});

export default connect(mapStateToProps, { getcandidate })(CandidateDashboard);